
    import Component from "vue-class-component";
    import {Prop} from "vue-property-decorator";
    import {
        ApplicationActions,
        EditDocumentSeedRequest,
        EditConcessionPermitSeedRequest,
        ResultError,
        ConcessionPermitDocumentApiModel,
        ShipsClient,
        ShipInfoModel,
        PositionsClient,
        PositionOption,
        LanguageOnPrintedEndorsement,
        StcwRulesClient,
        StcwRuleLightModel
    } from "@/api-client";
    import IssueDocumentInnerComponent from "@/features/applications/components/issue-document/IssueDocumentInnerComponent";
    import IssueDocumentSignature from "@/features/applications/components/issue-document/IssueDocumentSignature.vue";
    import DatePicker from "@/components/DatePicker.vue";
    import LabelOptional from "@/features/shared/components/LabelOptional.vue";
    import AdditionalInfoButton from "@/features/shared/components/AdditionalInfoButton.vue";
    import Multiselect from "vue-multiselect";
    import {debounceFn} from "debounce-decorator-ts";

    @Component({
        components: {
            AdditionalInfoButton,
            DatePicker,
            IssueDocumentSignature,
            LabelOptional,
            Multiselect
        }
    })
    export default class IssueConcessionPermit extends IssueDocumentInnerComponent {
        @Prop({required: true})
        issueDocumentDetails!: ConcessionPermitDocumentApiModel;
        @Prop({required: true})
        actions!: ApplicationActions;
        @Prop({required: true})
        errors!: ResultError;

        initialEditModel!: EditDocumentSeedRequest;
        shipsClient = new ShipsClient();
        positionsClient = new PositionsClient();
        stcwRulesClient = new StcwRulesClient();

        languageOnPrintOptions = LanguageOnPrintedEndorsement;

        validUntil = this.issueDocumentDetails.validUntil;
        issueDate = this.issueDocumentDetails.issueDate;
        additionalInfo = this.issueDocumentDetails.additionalInfo;
        languageOnPrint = this.issueDocumentDetails.languageOnPrint;
        selectedShip: ShipInfoModel | null = null;
        selectedPositions: PositionOption[] = [];
        selectedStcwRules: StcwRuleLightModel[] = [];
        shipsFromSearch: ShipInfoModel[] = [];
        positionOptions: PositionOption[] = [];
        stcwRulesOptions: StcwRuleLightModel[] = [];
        searching = false;

        async mounted() {
            await this.loadPositions();
            await this.loadStcwRules();

            // Load ship details if shipId exists
            if (this.issueDocumentDetails.shipId) {
                await this.loadShipById(this.issueDocumentDetails.shipId);
            }

            // Initialize selected positions from existing data after options are loaded
            if (this.issueDocumentDetails.positionIds && this.issueDocumentDetails.positionIds.length > 0) {
                const positionIds = this.issueDocumentDetails.positionIds;
                this.selectedPositions = this.positionOptions.filter(option =>
                    positionIds.includes(option.value)
                );
            }

            // Initialize selected STCW rules from existing data after options are loaded
            if (this.issueDocumentDetails.stcwRules && this.issueDocumentDetails.stcwRules.length > 0) {
                this.selectedStcwRules = this.issueDocumentDetails.stcwRules
                    .map(id => this.stcwRulesOptions.find(rule => rule.id === id))
                    .filter(rule => rule !== undefined) as StcwRuleLightModel[];
            }

            this.initialEditModel = this.getEditModel();
        }

        async loadPositions() {
            this.positionOptions = await this.positionsClient.activePositionOptions();
        }

        async loadStcwRules() {
            this.stcwRulesOptions = await this.stcwRulesClient.list();
        }

        async loadShipById(shipId: string) {
            // First get all ship options to find the ship name
            const shipOptionsResponse = await this.shipsClient.shipOptions();
            const shipOption = shipOptionsResponse.ships.find(s => s.value === shipId);

            if (shipOption) {
                // Use the ship name to search and get the full ShipInfoModel
                const searchResults = await this.shipsClient.search(shipOption.text);
                const ship = searchResults.find(s => s.id === shipId);

                if (ship) {
                    this.selectedShip = ship;
                    this.shipsFromSearch = [ship];
                }
            }
        }

        async searchShips(searchTerm: string) {
            this.searching = true;
            await this.searchShipsDebounced(searchTerm);
        }

        @debounceFn(500, "searchShipsFn")
        async searchShipsDebounced(searchTerm: string) {
            if (searchTerm.trim() !== '') {
                this.shipsFromSearch = await this.shipsClient.search(searchTerm);
                this.searching = false;
            } else {
                this.shipsFromSearch = [];
            }
        }

        get noResultText() {
            return this.searching ? this.$ls.Global_Placeholder_Searching : this.$ls.Global_Placeholder_NoResultsFound;
        }

        getShipDescription(ship: ShipInfoModel) {
            return `${ship.name} ${ship.imoNr}`;
        }

        removeStcwRule(rule: StcwRuleLightModel) {
            const index = this.selectedStcwRules.indexOf(rule);
            if (index > -1) {
                this.selectedStcwRules.splice(index, 1);
            }
        }

        getEditModel(): EditDocumentSeedRequest {
            const request = new EditConcessionPermitSeedRequest();
            request.additionalInfo = this.additionalInfo;
            request.issueDate = this.issueDate;
            request.validUntil = this.validUntil;
            // Use selected ship if available, otherwise preserve the original shipId
            request.shipId = this.selectedShip?.id ?? this.issueDocumentDetails.shipId;
            request.positionIds = this.selectedPositions.map(pos => pos.value);
            request.languageOnPrint = this.languageOnPrint;
            request.stcwRules = (this.selectedStcwRules || []).map(rule => rule.id);
            return request;
        }
    }
